import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { CreatePrimeArtifact } from '../../types/primeArtifact';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { fetchPrimeArtifactsHierarchy } from '../../store/actions/primeArtifactActions';
import styles from './ArtifactCreationDialog.module.css';
import { useArtifactTypes } from '../../hooks/useArtifactTypes';
import { useUser } from '../../contexts/UserContext';
import { PERMISSIONS } from '../../constants/permissions';
import { apiClient } from '../../services/apiClient';

interface ArtifactCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  parentId?: string | null;
  parentName?: string | null;
  isChild: boolean;
}

const ArtifactCreationDialog: React.FC<ArtifactCreationDialogProps> = ({
  isOpen,
  onClose,
  parentId = null,
  parentName = null,
  isChild,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, hasPermission } = useUser();
  const { artifactTypes } = useArtifactTypes();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [artifactTypeId, setArtifactTypeId] = useState('');
  const [stateTemplateId, setStateTemplateId] = useState<string>('');
  const [stateTemplates, setStateTemplates] = useState<Array<{ id: string; name: string; version?: string }>>([]);
  const [loadingStateTemplates, setLoadingStateTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set default artifact type when types are loaded
  useEffect(() => {
    if (isOpen && artifactTypes.length > 0 && !artifactTypeId) {
      setArtifactTypeId(artifactTypes[0].id);
    }
  }, [isOpen, artifactTypes, artifactTypeId]);

  useEffect(() => {
    const fetchStateTemplates = async () => {
      setLoadingStateTemplates(true);
      try {
        const response = await apiClient.get('/state-templates?include_deleted=false');
        const templates = response.data || [];
        setStateTemplates(templates.map((t: any) => ({
          id: t.id,
          name: t.name,
          version: t.version
        })));
      } catch (err) {
        console.error('Failed to fetch state templates:', err);
        // Don't set error for state templates as it's optional
        setStateTemplates([]);
      } finally {
        setLoadingStateTemplates(false);
      }
    };

    if (isOpen) {
      void fetchStateTemplates();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check authentication
    if (!isAuthenticated) {
      setError('You must be authenticated to create artifacts. Please log in.');
      return;
    }

    if (!hasPermission(PERMISSIONS.WRITE_ARTIFACTS)) {
      setError('You do not have permission to create artifacts.');
      return;
    }

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!artifactTypeId) {
      setError('Artifact type is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const selectedType = artifactTypes.find((type) => type.id === artifactTypeId);

      const newArtifact: CreatePrimeArtifact = {
        name: name.trim(),
        description: description.trim() || undefined,
        artifact_type_id: artifactTypeId,
        artifact_type_name: selectedType?.name || undefined,
        parent_ref: parentId || undefined,
        parent_name: parentName || undefined,
        state_template_id: stateTemplateId || undefined,
      };

      await PrimeArtifactService.create(newArtifact);

      // Refresh the artifact hierarchy
      await dispatch(fetchPrimeArtifactsHierarchy());

      // Reset form and close dialog
      setName('');
      setDescription('');
      setStateTemplateId('');
      onClose();
    } catch (err) {
      console.error('Failed to create artifact:', err);
      setError('Failed to create artifact. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.artifactCreationDialogOverlay}>
      <div className={styles.artifactCreationDialog}>
        <h2>{isChild ? 'Add Child Artifact' : 'Add Peer Artifact'}</h2>
        {parentName && (
          <p className={styles.parentInfo}>
            {isChild ? `Parent: ${parentName}` : `Sibling of: ${parentName}`}
          </p>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter artifact name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter artifact description (optional)"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="artifactType">Artifact Type *</label>
            <select
              id="artifactType"
              value={artifactTypeId}
              onChange={(e) => setArtifactTypeId(e.target.value)}
              required
            >
              {artifactTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="stateTemplate">State Template</label>
            <select
              id="stateTemplate"
              value={stateTemplateId}
              onChange={(e) => setStateTemplateId(e.target.value)}
              disabled={loadingStateTemplates}
            >
              <option value="">None (optional)</option>
              {stateTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}{template.version ? ` (v${template.version})` : ''}
                </option>
              ))}
            </select>
            {loadingStateTemplates && (
              <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
                Loading templates...
              </span>
            )}
          </div>

          <div className={styles.dialogActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArtifactCreationDialog;
